import { interactionEnded, onInteractionEnd } from '../interactionTermination'
import tinycolor from 'tinycolor2'

import { safeExecuteFunc, blurAll } from '../components/utils'
import RemoteCalibrator from '../core'
import { _setDebugControl } from './panelDebugControl'
import { phrases } from '../i18n/schema'
import { processInlineFormatting } from '../distance/markdownInstructionParser'
import { PanelState } from './panelState'
import { checkPermissions } from '../components/mediaPermission'
import {
  showTestPopup,
  hideResolutionSettingMessage,
} from '../components/popup'
import {
  hasStoredScreenSizeCacheEntry,
  loadValidScreenSizeCache,
  resolveCalibrateScreenSizeCacheBool,
} from '../screenSizeCache'

// Icons from Google Material UI
import Camera from '../media/photo-camera.svg'
import Phone from '../media/smartphone.svg'

import '../css/panel.scss'
import { setUpEasyEyesKeypadHandler } from '../extensions/keypadHandler'

RemoteCalibrator.prototype.removePanel = function () {
  if (!this._panelStatus.hasPanel) return false
  this._panel.panelObserver.unobserve(this._panel.panel)
  this._panel.panel.remove()

  this._panel.panel = null
  this._panel.panelObserver = null
  this._panel.panelTasks = []
  this._panel.panelParent = null
  this._panel.panelOptions = {}
  this._panel.panelCallback = null
  this._panel.panelResolve = null

  // clear panel state
  if (this._panelState) {
    console.log('[PanelState] Panel being removed')
    this._panelState.logState('Panel removed')
    this._panelState.reset()
    this._panelState = null
  }

  this._panelStatus.hasPanel = false
  this._panelStatus.panelFinished = false
  this._cameraSelectionDone = false
  this._distanceTrackingFullyInitialized = false
  _clearPanelIntervals(this)

  if (this._panelCallbackInteraction !== this._panelInteraction) {
    this._interactionLifecycle?.endScope(this._panelInteraction, 'cancelled')
    this._panelInteraction = null
  }
  return true
}

RemoteCalibrator.prototype.resetPanel = function (
  tasks = null,
  parent = null,
  options = null,
  callback = null,
  resolveOnFinish = null,
) {
  if (!this._panelStatus.hasPanel) return false

  const t = tasks || [...this._panel.panelTasks]
  const o = options || { ...this._panel.panelOptions }
  const c =
    callback && typeof callback === 'function'
      ? callback
      : this._panel.panelCallback
  const r = resolveOnFinish || this._panel.panelResolve

  // New parent
  if (parent !== null && parent !== this._panel.panelParent) {
    this.removePanel()
    return this.panel(t, parent, o, c, r)
  }

  // Current parent, just reset
  _clearPanelIntervals(this)
  return this.panel(t, this._panel.panelParent, o, c, r, true)
}

RemoteCalibrator.prototype.panel = async function (
  tasks,
  parent,
  panelOptions = {},
  callback = null,
  resolveOnFinish = null,
  __reset__ = false, // ! Not available to users
) {
  if (this._panelStatus.hasPanel ^ __reset__) return false
  /**
   * has rest
   * t   f no
   * t   t ok
   * f   f ok
   * f   t no
   */

  // Tasks
  if (!_validateTask(tasks)) {
    console.error('Invalid task name(s).')
    return false
  }

  // Parent
  const parentElement = document.querySelector(parent)
  if (!parentElement) {
    console.error('Cannot find the parent element.')
    return false
  }

  const lifecycle = this._interactionLifecycle
  if (__reset__) lifecycle?.endScope(this._panelInteraction, 'cancelled')
  let interaction = null
  const originalCallback = callback
  callback = data => {
    if (interactionEnded(this)) return
    this._panelCallbackInteraction = interaction
    let result
    try {
      result = originalCallback?.(data)
    } catch (error) {
      lifecycle?.endScope(interaction, 'failed')
      throw error
    }
    const finish = () => {
      if (this._panelCallbackInteraction === interaction)
        this._panelCallbackInteraction = null
      lifecycle?.endScope(interaction, 'completed')
      if (this._panelInteraction === interaction) this._panelInteraction = null
    }
    if (result?.then)
      return Promise.resolve(result).then(finish, error => {
        if (this._panelCallbackInteraction === interaction)
          this._panelCallbackInteraction = null
        lifecycle?.endScope(interaction, 'failed')
        console.error('[RC panel callback]', error)
      })
    else finish()
  }
  try {
    const options = Object.assign(
      {
        headline: phrases.RC_panelTitle[this.L],
        description: phrases.RC_panelIntro[this.L],
        showNextButton: false,
        nextHeadline: phrases.RC_panelTitleNext[this.L],
        nextDescription: phrases.RC_panelIntroNext[this.L],
        nextButton: phrases.RC_panelButton[this.L],
        color: '#3490de',
        debug: false,
        i18n: true,
        fullscreen: true,
        _demoActivateAll: false, // ! Private
      },
      panelOptions,
    )

    // Enter fullscreen BEFORE checking the screen-size cache. The cached monitor
    // fingerprint stores the window position (screenLeft/screenTop), which reads
    // (0,0) only in fullscreen — the state in which the cache is always saved
    // (screenSize calibration forces fullscreen). If we checked while still
    // windowed, the position would be offset by the browser chrome (e.g.
    // top=122) and never match, so the Size button would wrongly appear even
    // with a valid cache.
    await this.getFullscreen(options.fullscreen)
    if (interactionEnded(this)) return false

    // If a screenSize task is present and its cache is enabled with a valid
    // entry for the current monitor, silently retrieve the size and drop the
    // Size button so it never appears in the panel. (EasyEyes
    // _calibrateScreenSizeCacheBool, default TRUE.)
    tasks = _silentlyResolveCachedScreenSize(this, tasks)

    // The fullscreen transition (notably on macOS) can take a moment to settle,
    // so window.screenLeft/Top may still report the windowed position right
    // after getFullscreen resolves. If a cacheable screenSize task survived but
    // a cache entry actually exists, wait for the geometry to stabilize and try
    // once more before giving up and showing the Size button.
    if (_hasCacheableScreenSizeTask(tasks) && hasStoredScreenSizeCacheEntry()) {
      await _waitForFullscreenGeometrySettled()
      if (interactionEnded(this)) return false
      tasks = _silentlyResolveCachedScreenSize(this, tasks)
    }

    // If pre-resolution (e.g. a valid screen-size cache) leaves no tasks to
    // perform, there is nothing to render. Previously the panel still drew its
    // heading/description but produced zero step buttons (and no Next button by
    // default), stranding the participant on a "press the button" page with
    // nothing to click. Instead, fire the completion callback and resolve so the
    // caller (EasyEyes) proceeds straight to the experiment. The desired
    // behavior maps cleanly here:
    //   - size cached + distance requested  → trackDistance keeps the list
    //     non-empty, so the Distance button still shows (handled below).
    //   - size cached + distance not requested → list is now empty, so we skip
    //     the whole Size & Distance page instead of showing a buttonless page.
    if (tasks.length === 0) {
      console.log(
        '[panel] No tasks to display — all requested calibrations already satisfied (e.g. cached screen size). Skipping the panel page and auto-completing.',
      )
      this._panelStatus.hasPanel = false
      this._panelStatus.panelFinished = true
      await callback({ timestamp: performance.now() })
      return resolveOnFinish === null ? true : resolveOnFinish
    }

    interaction = lifecycle?.beginScope('calibration-panel')
    this._panelInteraction = interaction

    // initialize panel state for tracking
    this._panelState = new PanelState()
    this._panelState.initFromTasks(tasks)
    console.log(
      '[PanelState] Panel initialized with tasks:',
      tasks.map(t => (typeof t === 'string' ? t : t.name)),
    )
    this._panelState.logState('Panel initialized')

    // Set theme color
    const darkerColor = tinycolor(options.color).darken(20).toString()
    document.documentElement.style.setProperty(
      '--rc-panel-theme-color',
      options.color,
    )
    document.documentElement.style.setProperty(
      '--rc-panel-darken-color',
      darkerColor,
    )
    document.documentElement.style.setProperty(
      '--rc-panel-theme-color-semi',
      `${options.color}66`,
    )
    document.documentElement.style.setProperty(
      '--rc-panel-darken-color-semi',
      `${darkerColor}88`,
    )

    const panel = document.createElement('div')
    panel.className = panel.id = 'rc-panel'
    if (this.LD === this._CONST.RTL) panel.className += ' rc-lang-rtl'
    else panel.className += ' rc-lang-ltr'

    if (options.i18n) {
      panel.innerHTML += `<div class="rc-panel-language-parent" id="rc-panel-language-parent"></div>`
    }
    panel.innerHTML += `<h1 class="rc-panel-title" id="rc-panel-title">${processInlineFormatting(
      options.headline,
    )}</h1>`
    panel.innerHTML += `<p class="rc-panel-description" id="rc-panel-description">${processInlineFormatting(
      options.description,
    )}</p>`
    panel.innerHTML += '<div class="rc-panel-steps" id="rc-panel-steps"></div>'

    // --- Camera selection before panel is visible ---
    // If any task is trackDistance and camera hasn't been selected yet,
    // run the full webcam pipeline now, before appending the panel to the DOM.
    if (!this._cameraSelectionDone) {
      const tdTask = tasks.find(
        t => (typeof t === 'string' ? t : t.name) === 'trackDistance',
      )
      if (tdTask) {
        const tdOpts = typeof tdTask === 'object' ? tdTask.options || {} : {}
        await _runCameraSelectionBeforePanel(this, tdOpts)
      }
    }

    if (interactionEnded(this)) return false
    hideResolutionSettingMessage()

    if (!__reset__) parentElement.appendChild(panel)
    else parentElement.replaceChild(panel, this._panel.panel) // ! reset

    const steps = panel.querySelector('#rc-panel-steps')
    const panelObserver = new ResizeObserver(() => {
      _setStepsClassesSL(steps, panel.offsetWidth, this.LD)
    })
    panelObserver.observe(panel)
    _setStepsClassesSL(steps, panel.offsetWidth, this.LD)

    if (tasks.length === 0) {
      steps.className += ' rc-panel-no-steps'
    } else {
      for (const t in tasks) {
        const b = _newStepBlock(this, t, tasks[t], options)
        steps.appendChild(b)
      }
    }

    if (options.showNextButton || options._demoActivateAll)
      steps.appendChild(_nextStepBlock(tasks.length, options))

    // Activate the first one
    const current = { index: 0, finished: [] }
    _activateStepAt(this, current, tasks, options, callback)

    this._panel.panel = panel
    this._panel.panelObserver = panelObserver
    this._panel.panelTasks = tasks
    this._panel.panelParent = parent

    const tempOptions = { ...options }
    if (options.headline === phrases.RC_panelTitle[this.L])
      tempOptions.headline = undefined
    if (options.description === phrases.RC_panelIntro[this.L])
      tempOptions.description = undefined
    if (options.nextHeadline === phrases.RC_panelTitleNext[this.L])
      tempOptions.nextHeadline = undefined
    if (options.nextDescription === phrases.RC_panelIntroNext[this.L])
      tempOptions.nextDescription = undefined
    if (options.nextButton === phrases.RC_panelButton[this.L])
      tempOptions.nextButton = undefined

    this._panel.panelOptions = tempOptions

    this._panel.panelCallback = originalCallback
    this._panel.panelResolve = resolveOnFinish

    this._panelStatus.hasPanel = true
    this._panelStatus.panelFinished = false

    // Simple space key blocking - just add event listener to the panel element itself
    panel.addEventListener('keydown', e => {
      if (e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
      }
    })

    if (options.i18n)
      _setLanguagePicker(
        this,
        document.querySelector('#rc-panel-language-parent'),
        darkerColor,
      )

    if (options.debug) _setDebugControl(this, panel, tasks, callback)
    if (resolveOnFinish === null) resolveOnFinish = true

    return new Promise(resolve => {
      const release = onInteractionEnd(this, () => resolve(false))
      const _ = setInterval(() => {
        if (this._panelStatus.panelFinished) {
          clearInterval(_)
          release()
          resolve(resolveOnFinish)
        }
      }, 100)
      this._panelStatus.panelResolveIntervals.push(_)
    })
  } catch (error) {
    lifecycle?.endScope(interaction, 'failed')
    if (this._panelInteraction === interaction) this._panelInteraction = null
    throw error
  }
}

/**
 * USE
 * 0 - Nothing
 * 1 - Screen
 * 2 - Screen and webcam
 * 3 - Screen, webcam, and smartphone
 */
const _validTaskList = {
  screenSize: {
    use: 1,
    name: phrases.RC_screenSize?.['en'],
    phraseHandle: 'RC_screenSize',
  },
  measureDistance: {
    use: 1,
    name: phrases.RC_viewingDistance?.['en'],
    phraseHandle: 'RC_viewingDistance',
  },
  trackDistance: {
    use: 2,
    name: phrases.RC_distanceTracking?.['en'],
    phraseHandle: 'RC_distanceTracking',
  },
  trackGaze: {
    use: 2,
    name: phrases.RC_gazeTracking?.['en'],
    phraseHandle: 'RC_gazeTracking',
  },
  performance: {
    use: 1,
    name: phrases.RC_performance?.['en'],
    phraseHandle: 'RC_performance',
  },
}
const _validTaskListNames = Object.keys(_validTaskList)

const _validateTask = task => {
  if (!Array.isArray(task)) return false
  for (const t of task) {
    if (
      typeof t === 'object' &&
      (t === null || !_validTaskListNames.includes(t.name))
    )
      return false
    if (typeof t === 'string' && !_validTaskListNames.includes(t)) return false
  }
  return true
}

const _newStepBlock = (RC, index, task, options) => {
  const useCode = _validTaskList[_getTaskName(task)].use
  let use
  let useTip

  switch (useCode) {
    case 0:
      use = ''
      useTip = ''
      break
    case 1:
      use = ''
      useTip = ''
      break
    case 2:
      use = Camera
      useTip = phrases.RC_panelUsesWebcam[RC.L]
      break
    case 3:
      use = Camera + Phone
      useTip = phrases.RC_panelUsesWebcamPhone[RC.L]
      break
    default:
      use = ''
      useTip = ''
      break
  }

  const b = document.createElement('button')
  b.className = `rc-panel-step rc-panel-step-todo${
    options._demoActivateAll
      ? ' rc-panel-step-active'
      : ' rc-panel-step-inactive'
  }`
  b.dataset.index = index
  b.innerHTML = `${use.length ? `<p class="rc-panel-step-use">${use}</p>` : ''}<p class="rc-panel-step-name">${Number(index) + 1}&nbsp;&nbsp;${
    phrases[_validTaskList[_getTaskName(task)].phraseHandle][RC.L]
  }</p>${use.length ? `<p class="rc-panel-step-use-tip">${use} ${useTip}</p>` : ''}`
  // b.disabled = true
  return b
}

const _nextStepBlock = (index, options) => {
  const b = document.createElement('button')
  b.className = 'rc-panel-step rc-panel-next-button rc-panel-step-inactive'
  b.dataset.index = index
  b.innerHTML = `<p class="rc-panel-step-name">${options.nextButton}</p>`
  // b.disabled = false
  return b
}

const _setStepsClassesSL = (steps, panelWidth, LD) => {
  if (panelWidth < 640) {
    steps.classList.add('rc-panel-steps-s')
    steps.classList.remove('rc-panel-steps-l')

    for (const e of steps.childNodes) {
      e.classList.add(`rc-lang-${LD.toLowerCase()}`)
    }
  } else {
    steps.classList.add('rc-panel-steps-l')
    steps.classList.remove('rc-panel-steps-s')

    for (const e of steps.childNodes) {
      e.classList.remove('rc-lang-ltr')
      e.classList.remove('rc-lang-rtl')
    }
  }
}

const _activateStepAt = (RC, current, tasks, options, finalCallback) => {
  const panelState = RC._panelState

  document.querySelectorAll('.rc-panel-step').forEach((e, ind) => {
    const eIndex = Number(e.dataset.index)

    if (!options._demoActivateAll) {
      // Default situation
      if (eIndex === current.index) {
        e.classList.replace('rc-panel-step-inactive', 'rc-panel-step-active')
        e.focus()

        if (eIndex !== tasks.length) {
          if (eIndex === tasks.length - 1 && !options.showNextButton) {
            // Last task without next button
            e.onclick = () => {
              const currentTaskId = _getTaskName(tasks[current.index])

              // Mark task as active
              if (panelState) {
                console.log(`[PanelState] Starting task: ${currentTaskId}`)
                panelState.setActive(currentTaskId)
                panelState.logState(`Task started: ${currentTaskId}`)
              }

              RC[currentTaskId](
                ..._getTaskOptionsCallbacks(
                  tasks[current.index],
                  // Fixed task callback - receives data from task
                  data => {
                    _finishStepAt(current.index)

                    // Track completion and log state
                    if (panelState) {
                      console.log(
                        `[PanelState] Task completed: ${currentTaskId}`,
                      )
                      panelState.completeTask(currentTaskId, data)
                      panelState.logState(`Task completed: ${currentTaskId}`)
                    }
                  },
                  finalCallback,
                  // Fixed final callback
                  () => {
                    console.log(
                      '[PanelState] All tasks completed - panel finished',
                    )
                    RC._panelStatus.panelFinished = true
                    if (panelState) {
                      panelState.logState('All tasks completed')
                    }
                  },
                ),
              )
            }
          } else {
            // Interim tasks
            e.onclick = () => {
              const currentTaskId = _getTaskName(tasks[current.index])

              // Mark task as active
              if (panelState) {
                console.log(`[PanelState] Starting task: ${currentTaskId}`)
                panelState.setActive(currentTaskId)
                panelState.logState(`Task started: ${currentTaskId}`)
              }

              RC[currentTaskId](
                ..._getTaskOptionsCallbacks(
                  tasks[current.index],
                  // Fixed task callback - receives data from task
                  data => {
                    _finishStepAt(current.index)

                    // Track completion and log state
                    if (panelState) {
                      console.log(
                        `[PanelState] Task completed: ${currentTaskId}`,
                      )
                      panelState.completeTask(currentTaskId, data)
                      panelState.logState(`Task completed: ${currentTaskId}`)
                    }

                    current.index++
                    _activateStepAt(RC, current, tasks, options, finalCallback)
                  },
                ),
              )
            }
          }

          setUpEasyEyesKeypadHandler(
            e,
            RC.keypadHandler,
            null,
            true,
            ['return'],
            RC,
          )
        } else if (eIndex === tasks.length && options.showNextButton) {
          // All tasks finished with next button
          // Change headline and description
          const { headline, nextHeadline, description, nextDescription } =
            options
          if (headline !== nextHeadline)
            document.querySelector('#rc-panel-title').innerHTML =
              processInlineFormatting(nextHeadline)
          if (description !== nextDescription)
            document.querySelector('#rc-panel-description').innerHTML =
              processInlineFormatting(nextDescription)

          e.onclick = () => {
            RC._panelStatus.panelFinished = true
            safeExecuteFunc(finalCallback, { timestamp: performance.now() })
          }

          setUpEasyEyesKeypadHandler(
            e,
            RC.keypadHandler,
            null,
            true,
            ['return'],
            RC,
          )
        }
      }
    } else {
      // Demo active all
      e.onclick = () => {
        RC[_getTaskName(tasks[ind])](..._getTaskOptionsCallbacks(tasks[ind]))
        _finishStepAt(ind)
        current.finished.push(_getTaskName(tasks[ind]))
        // Check if all finished
        for (const t of tasks) {
          if (!current.finished.includes(_getTaskName(t))) return
        }
        // If so, activate the next step button
        const finalButton = document.querySelector('.rc-panel-next-button')
        finalButton.classList.replace(
          'rc-panel-step-inactive',
          'rc-panel-step-active',
        )
        finalButton.onclick = () => {
          RC._panelStatus.panelFinished = true
          safeExecuteFunc(finalCallback, { timestamp: performance.now() })
        }
      }

      setUpEasyEyesKeypadHandler(
        e,
        RC.keypadHandler,
        null,
        true,
        ['return'],
        RC,
      )
    }
  })
}

const _finishStepAt = index => {
  const steps = document.querySelectorAll('.rc-panel-step')

  for (const e of steps) {
    if (Number(e.dataset.index) === index) {
      e.classList.replace('rc-panel-step-todo', 'rc-panel-step-finished')
      e.classList.replace('rc-panel-step-active', 'rc-panel-step-inactive')
    }
  }
}

const _getTaskName = task => {
  if (typeof task === 'string') return task
  return task.name
}

/**
 * If a screenSize task is in the list and (a) the cache option is enabled
 * and (b) a valid cached entry exists for the current monitor, silently
 * invoke RC.screenSize(...) so the data is restored and the task's user
 * callback fires, then drop the task from the rendered panel so the Size
 * button never appears. Falls back to leaving the task in place if the
 * cache is missing, disabled, or the silent invocation throws.
 */
/**
 * Whether the task list still contains a screenSize task whose cache option is
 * enabled (i.e. one that could yet be served from a valid cache).
 */
const _hasCacheableScreenSizeTask = tasks =>
  tasks.some(task => {
    if (_getTaskName(task) !== 'screenSize') return false
    const opts =
      typeof task === 'object' && task !== null ? task.options || {} : {}
    return resolveCalibrateScreenSizeCacheBool(opts)
  })

/**
 * Wait until the browser window geometry (screenLeft/screenTop) stops changing,
 * which signals that a fullscreen transition has finished and the cached
 * monitor fingerprint can be compared reliably. Resolves early once geometry is
 * stable for a few frames, and is capped by maxMs so it never blocks for long.
 */
const _waitForFullscreenGeometrySettled = async (maxMs = 1200) => {
  if (typeof requestAnimationFrame !== 'function') return
  const start = performance.now()
  let last = null
  let stableFrames = 0
  while (performance.now() - start < maxMs) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => requestAnimationFrame(() => resolve()))
    const cur = `${window.screenLeft ?? window.screenX},${
      window.screenTop ?? window.screenY
    }`
    if (cur === last) {
      if (++stableFrames >= 3) break
    } else {
      stableFrames = 0
      last = cur
    }
  }
}

const _silentlyResolveCachedScreenSize = (RC, tasks) => {
  const remaining = []
  for (const task of tasks) {
    if (_getTaskName(task) !== 'screenSize') {
      remaining.push(task)
      continue
    }

    const isObjectTask = typeof task === 'object' && task !== null
    const taskOptions = isObjectTask ? task.options || {} : {}

    if (!resolveCalibrateScreenSizeCacheBool(taskOptions)) {
      remaining.push(task)
      continue
    }

    const cached = loadValidScreenSizeCache()
    if (!cached) {
      remaining.push(task)
      continue
    }

    console.log(
      '[panel] screenSize cache available — silently retrieving size from localStorage and removing the Size button from the panel',
    )
    try {
      const userCallback = isObjectTask ? task.callback || null : null
      RC.screenSize(taskOptions, userCallback)
    } catch (err) {
      console.log(
        '[panel] Silent screenSize cache retrieval failed — falling back to showing the Size button',
        err,
      )
      remaining.push(task)
    }
  }
  return remaining
}

const _getTaskOptionsCallbacks = (
  task,
  fixedTaskCallback,
  finalCallback = null,
  fixedFinalCallback = null,
) => {
  if (typeof task === 'string')
    task = {
      name: task,
    }

  const getFinalCallbacks = data => {
    // Task - pass data for state tracking
    safeExecuteFunc(fixedTaskCallback, data)
    // Panel
    safeExecuteFunc(finalCallback, { timestamp: performance.now() })
    safeExecuteFunc(fixedFinalCallback)
  }

  if (['screenSize', 'measureDistance', 'performance'].includes(task.name)) {
    return [
      task.options || {},
      data => {
        safeExecuteFunc(task.callback, data)
        getFinalCallbacks(data)
      },
    ]
  }

  if ('trackGaze' === task.name) {
    return [
      task.options || {},
      data => {
        safeExecuteFunc(task.callbackOnCalibrationEnd, data)
        getFinalCallbacks(data)
      },
      task.callbackTrack || null,
    ]
  }

  if ('trackDistance' === task.name) {
    return [
      task.options || {},
      data => {
        safeExecuteFunc(task.callbackStatic, data)
        getFinalCallbacks(data)
      },
      task.callbackTrack || null,
    ]
  }
}

/**
 * Initialise the webcam pipeline and run camera selection before the panel
 * step buttons become active.  Mirrors the init sequence that trackDistance
 * performs (gazeTracker._init → permissions → loadModel → beginVideo →
 * showTestPopup) so that when trackDistance runs later it can skip all of
 * that and go straight to calibration.
 */
const _runCameraSelectionBeforePanel = async (RC, tdOpts) => {
  // 1. gazeTracker init (distance mode) — sets desired resolution/Hz
  if (!RC.gazeTracker.checkInitialized('distance')) {
    RC.gazeTracker._init(
      {
        toFixedN: 1,
        showVideo: true,
        showFaceOverlay: false,
        desiredCameraResolution: tdOpts.calibrateDistanceCameraResolution,
        desiredCameraHz: tdOpts.calibrateDistanceCameraHz,
      },
      'distance',
    )
  }

  // 2. Camera permissions
  let permMessage = `${phrases.RC_requestCamera[RC.L]}`
  if (!tdOpts.saveSnapshots) {
    permMessage += `<br />${phrases.RC_privacyCamera[RC.L]}`
  }
  await checkPermissions(RC, permMessage)

  // 3. Show "Starting..." in the same style as the resolution message.
  // Ensure the standard EasyEyes gray (#eee) background is painted under
  // the message so this page matches every other RC page. Without this
  // the body's default (white) shows through and "Connecting to your
  // webcam(s) ..." looks noticeably lighter than neighboring pages.
  const _backgroundAddedHere = RC.background === null
  if (_backgroundAddedHere) RC._addBackground()
  const startingMsg = document.createElement('div')
  startingMsg.id = 'rc-starting-message'
  startingMsg.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9999999999;
    text-align: center;
    color: #666;
    font-style: normal;
    font-size: 1.6rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    pointer-events: none;
    user-select: none;
  `
  startingMsg.innerHTML = phrases.RC_starting[RC.L]
  document.body.appendChild(startingMsg)

  // 4. One camera session owns model load + video start. Failures must
  // not reject into the consumer's global handler or leave this promise
  // unsettled. Continue to the Choose Camera popup, which handles the
  // no-camera case itself.
  const pipWidthPx =
    RC._CONST.N.VIDEO_W[RC.isMobile.value ? 'MOBILE' : 'DESKTOP']
  try {
    await RC.gazeTracker.startCameraSession({
      videoOnly: true,
      pipWidthPx,
      requireModel: false,
    })
  } catch (error) {
    console.error('[RC.panel] Camera session failed to start:', error)
  }

  // Remove "Starting..." message
  startingMsg.remove()

  // 4. Camera selection + resolution
  const cameraResult = await showTestPopup(RC, null, tdOpts)
  if (cameraResult?.experimentEnded) {
    console.log('Experiment ended - no cameras detected')
  }

  // 5. Only mark done if a camera was actually selected
  if (!cameraResult?.experimentEnded) {
    RC._cameraSelectionDone = true
  }

  // Clean up any leftover text from camera selection
  const leftoverLoading = document.getElementById('camera-loading-text')
  if (leftoverLoading) leftoverLoading.remove()
  hideResolutionSettingMessage()

  // Tear down the gray background we added before "Starting..." so the
  // panel renders cleanly against its own background below.
  if (_backgroundAddedHere) RC._removeBackground()

  // 6. Hide video so it doesn't show during screenSize (credit card step)
  RC.showVideo(false)
  const vc = document.getElementById('webgazerVideoContainer')
  if (vc) vc.style.display = 'none'
}

const _clearPanelIntervals = RC => {
  RC._panelStatus.panelResolveIntervals.map(i => clearInterval(i))
  RC._panelStatus.panelResolveIntervals = []
}

const _setLanguagePicker = (RC, parent, darkerColor) => {
  let langInner = `<select name="rc-lang" id="rc-panel-lang-picker" style="color: ${darkerColor} !important">`
  for (const lang of RC.supportedLanguages)
    if (RC.L === lang.language)
      langInner += `<option value="${lang.language}" selected>${lang.languageNameNative}</option>`
  for (const lang of RC.supportedLanguages)
    if (RC.L !== lang.language)
      langInner += `<option value="${lang.language}">${lang.languageNameNative}</option>`
  langInner += '</select>'
  parent.innerHTML = langInner

  document.querySelector('#rc-panel-lang-picker').onchange = () => {
    RC.newLanguage(document.querySelector('#rc-panel-lang-picker').value)
    RC.resetPanel()
  }
}
